import { useCallback, useLayoutEffect, useRef, useState } from 'react';

const VIEWPORT_MARGIN = 12;
const TRIGGER_GAP = 6;
// Below this a calendar is unusable, so stop shrinking and let it scroll.
const MIN_HEIGHT = 220;

/**
 * Positions a popover against its trigger, in viewport coordinates.
 *
 * Anchoring below (or above) the trigger only works while that side has room.
 * On a short window neither does, and since the popover's footer is last in the
 * flow it's the Apply/Cancel row that drops off screen. A field sitting in the
 * middle of a centred modal is the worst case: ~300px each way, but ~620px free
 * in the viewport overall.
 *
 * So: below if it fits, else above if it fits, else float it — centred on the
 * trigger and clamped to the viewport, using the space the sides don't have.
 * Only when the popover can't fit the viewport at all does it cap and scroll.
 *
 * Fixed positioning also means no ancestor's overflow can clip it, which is why
 * position is tracked on scroll as well as resize.
 */
export default function usePopoverPosition(open, triggerRef, popoverRef) {
  const [position, setPosition] = useState(null);
  const rafRef = useRef(null);

  const measure = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (!trigger || !popover) return;

    // Natural height with any previous cap lifted, so a measurement never
    // inherits the constraint the last one applied.
    const capped = popover.style.maxHeight;
    popover.style.maxHeight = 'none';
    const natural = popover.offsetHeight;
    const width = popover.offsetWidth;
    popover.style.maxHeight = capped;

    const rect = trigger.getBoundingClientRect();
    const { innerHeight: vh, innerWidth: vw } = window;
    const below = vh - rect.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;
    const above = rect.top - TRIGGER_GAP - VIEWPORT_MARGIN;

    let top;
    let maxHeight = null;
    if (natural <= below) {
      top = rect.bottom + TRIGGER_GAP;
    } else if (natural <= above) {
      top = rect.top - TRIGGER_GAP - natural;
    } else {
      // Overlapping the trigger is unavoidable here, so anchor to the roomier
      // side and let the clamp push it back in — it still reads as attached to
      // the field, and only shifts by as much as the viewport demands.
      maxHeight = Math.max(MIN_HEIGHT, Math.min(natural, vh - VIEWPORT_MARGIN * 2));
      const anchored =
        below >= above ? rect.bottom + TRIGGER_GAP : rect.top - TRIGGER_GAP - maxHeight;
      top = Math.min(Math.max(VIEWPORT_MARGIN, anchored), vh - maxHeight - VIEWPORT_MARGIN);
    }

    const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.left), vw - width - VIEWPORT_MARGIN);
    setPosition({ top, left: Math.max(VIEWPORT_MARGIN, left), maxHeight });
  }, [triggerRef, popoverRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    measure();
    const onChange = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', onChange);
    // Capture, so scrolling any container the field sits in keeps it anchored.
    window.addEventListener('scroll', onChange, true);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [open, measure]);

  return position;
}
